/**
 * IncomingCallModal — Phase 2, Task 6.
 *
 * Renders a MUI Dialog whenever `useCallController().incoming` is non-null
 * (i.e. someone is calling this user). The modal shows the caller's name plus
 * Accept and Decline actions.
 *
 * Ringtone: the RECEIVER hears a melodic phone-app jingle (not a beep) — the
 * `'melody'` tone from `useCallTone`, which loops while the modal is open and
 * no-ops in test environments without WebAudio. (The CALLER hears the
 * `'ringback'` "ring-ring" instead — see CallWindow.)
 *
 * See docs/calls.md for the three-plane calling architecture.
 */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";

import { useCallController } from "./CallProvider.js";
import { useCallTone } from "./useCallTone.js";

// ─── Component ────────────────────────────────────────────────────────────────

export function IncomingCallModal(): JSX.Element | null {
  const { incoming, accept, decline } = useCallController();

  const isOpen = incoming !== null;
  // Receiver hears the melodic ringtone (a phone-app jingle, not a beep).
  useCallTone(isOpen, "melody");

  return (
    <Dialog
      open={isOpen}
      // No onClose — callers must use Accept or Decline.
      aria-labelledby="incoming-call-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: 320,
          textAlign: "center",
        },
      }}
    >
      <DialogTitle id="incoming-call-title" sx={{ pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            pt: 1,
          }}
        >
          <Box
            sx={(theme) => ({
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 0 8px ${theme.palette.success.light}33`,
            })}
          >
            <CallIcon sx={{ fontSize: 32, color: "success.contrastText" }} />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
            INCOMING CALL
          </Typography>

          <Typography variant="h6" fontWeight={600}>
            {incoming?.from_user_name ?? ""}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }} />

      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          pb: 3,
          pt: 2,
        }}
      >
        <Button
          variant="contained"
          color="error"
          size="large"
          aria-label="Decline"
          startIcon={<CallEndIcon />}
          onClick={() => void decline()}
          sx={{ borderRadius: 6, px: 3 }}
        >
          Decline
        </Button>

        <Button
          variant="contained"
          color="success"
          size="large"
          aria-label="Accept"
          startIcon={<CallIcon />}
          onClick={() => void accept()}
          sx={{ borderRadius: 6, px: 3 }}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
}
